import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Rocket, FolderKanban, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { hubAPI } from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * Gestion de l'accès Hub Projets d'un utilisateur.
 * Modes : none / restricted (avec choix d'espaces entiers + projets nominatifs) / full.
 */
export function HubAccessButton({ targetUser, onChanged }) {
    const [open, setOpen] = useState(false);
    const [access, setAccess] = useState(targetUser?.hub_access || 'none');
    const [selectedProjectIds, setSelectedProjectIds] = useState(targetUser?.hub_project_ids || []);
    const [selectedSpaceIds, setSelectedSpaceIds] = useState(targetUser?.hub_space_ids || []);
    const [spaces, setSpaces] = useState([]);
    const [projects, setProjects] = useState([]);
    const [themes, setThemes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        (async () => {
            setLoading(true);
            try {
                const [spR, thR] = await Promise.all([
                    hubAPI.listSpaces(),
                    hubAPI.listThemes(),
                ]);
                setSpaces(spR.data);
                setThemes(thR.data);
                const allProjects = [];
                for (const s of (spR.data || [])) {
                    try {
                        const pr = await hubAPI.listProjects(s.id, { status: 'active' });
                        pr.data.forEach((p) => allProjects.push({ ...p, _space: s }));
                    } catch { /* ignore */ }
                }
                setProjects(allProjects);
            } catch (e) {
                toast.error("Impossible de charger les données Hub");
            } finally {
                setLoading(false);
            }
        })();
    }, [open]);

    useEffect(() => {
        setAccess(targetUser?.hub_access || 'none');
        setSelectedProjectIds(targetUser?.hub_project_ids || []);
        setSelectedSpaceIds(targetUser?.hub_space_ids || []);
    }, [targetUser]);

    const toggleProject = (pid) => {
        setSelectedProjectIds((prev) => prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid]);
    };
    const toggleSpace = (sid) => {
        setSelectedSpaceIds((prev) => prev.includes(sid) ? prev.filter((x) => x !== sid) : [...prev, sid]);
    };

    const submit = async () => {
        setSaving(true);
        try {
            const payload = access === 'restricted'
                ? { project_ids: selectedProjectIds, space_ids: selectedSpaceIds }
                : { project_ids: [], space_ids: [] };
            await hubAPI.setUserAccess(targetUser.id, access, payload.project_ids, payload.space_ids);
            toast.success('Accès Projets mis à jour');
            setOpen(false);
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur');
        } finally {
            setSaving(false);
        }
    };

    // Résumé bouton
    const currentLabel = (() => {
        if (targetUser?.hub_access === 'full') return 'Projets : Complet';
        if (targetUser?.hub_access === 'restricted') {
            const nS = targetUser.hub_space_ids?.length || 0;
            const nP = targetUser.hub_project_ids?.length || 0;
            const parts = [];
            if (nS) parts.push(`${nS} espace${nS > 1 ? 's' : ''}`);
            if (nP) parts.push(`${nP} projet${nP > 1 ? 's' : ''}`);
            return `Projets : ${parts.join(' + ') || 'aucun'}`;
        }
        return 'Projets';
    })();

    const currentClass = targetUser?.hub_access === 'full'
        ? 'text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border-indigo-300'
        : targetUser?.hub_access === 'restricted'
            ? 'text-purple-700 bg-purple-50 hover:bg-purple-100 border-purple-300'
            : 'text-slate-600 hover:bg-indigo-50';

    // Filtre les projets pour cacher ceux dont l'espace est déjà entièrement coché (redondant)
    const projectsToShow = projects.filter((p) => !selectedSpaceIds.includes(p.space_id));

    return (
        <>
            <Button
                variant="outline"
                size="sm"
                onClick={() => setOpen(true)}
                className={currentClass}
                data-testid={`toggle-hub-access-${targetUser.id}`}
                title="Configurer l'accès aux Projets (Hub)"
            >
                <Rocket className="w-4 h-4 mr-1" />
                {currentLabel}
            </Button>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="hub-access-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><Rocket className="w-5 h-5 text-indigo-500" /> Accès Projets — {targetUser?.first_name} {targetUser?.last_name}</DialogTitle>
                        <DialogDescription>
                            Configurez le niveau d&apos;accès de cet utilisateur au module Projets (Hub).
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-2">
                        <AccessOption
                            value="none"
                            selected={access}
                            onSelect={setAccess}
                            title="Aucun accès"
                            description="Le menu Projets est masqué pour cet utilisateur."
                        />
                        <AccessOption
                            value="restricted"
                            selected={access}
                            onSelect={setAccess}
                            title="Accès limité"
                            description="Choisissez ci-dessous des espaces entiers (l'utilisateur verra tous les projets de ces espaces, actuels ET futurs) et/ou des projets nominatifs. Lecture et participation uniquement, aucune création."
                        />
                        <AccessOption
                            value="full"
                            selected={access}
                            onSelect={setAccess}
                            title="Accès complet"
                            description="L'utilisateur voit tous les projets de tous les espaces, peut créer et modifier ses propres projets."
                        />
                    </div>

                    {access === 'restricted' && (
                        <div className="mt-4 border-t pt-4 space-y-4">
                            {/* Espaces */}
                            <div>
                                <Label className="text-sm font-semibold flex items-center gap-1.5">
                                    <FolderKanban className="w-4 h-4 text-indigo-500" />
                                    Espaces attribués ({selectedSpaceIds.length})
                                </Label>
                                <p className="text-[11px] text-slate-500 mt-0.5">Accès complet à tous les projets de l&apos;espace, y compris ceux à venir.</p>
                                {loading ? (
                                    <div className="text-xs text-slate-400 p-3">Chargement…</div>
                                ) : spaces.length === 0 ? (
                                    <div className="text-xs text-slate-400 italic p-3">Aucun espace disponible.</div>
                                ) : (
                                    <div className="mt-2 grid sm:grid-cols-2 gap-2">
                                        {spaces.map((s) => (
                                            <label
                                                key={s.id}
                                                className={cn(
                                                    "flex items-center gap-2 p-2 rounded-md border cursor-pointer transition-colors",
                                                    selectedSpaceIds.includes(s.id)
                                                        ? "bg-indigo-50 border-indigo-300"
                                                        : "border-slate-200 hover:bg-slate-50"
                                                )}
                                                data-testid={`hub-access-space-label-${s.id}`}
                                            >
                                                <Checkbox
                                                    checked={selectedSpaceIds.includes(s.id)}
                                                    onCheckedChange={() => toggleSpace(s.id)}
                                                    data-testid={`hub-access-space-${s.id}`}
                                                />
                                                <FolderKanban className="w-4 h-4 text-indigo-400 shrink-0" />
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm text-slate-800 font-medium truncate">{s.name}</div>
                                                    {s.description && <div className="text-[10px] text-slate-400 truncate">{s.description}</div>}
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Projets nominatifs */}
                            <div>
                                <Label className="text-sm font-semibold flex items-center gap-1.5">
                                    <FileText className="w-4 h-4 text-purple-500" />
                                    Projets ponctuels ({selectedProjectIds.length})
                                </Label>
                                <p className="text-[11px] text-slate-500 mt-0.5">Accès à des projets précis, indépendamment de l&apos;espace. (Les projets des espaces déjà cochés sont masqués — inutile de les recocher.)</p>
                                {loading ? (
                                    <div className="text-xs text-slate-400 p-3">Chargement…</div>
                                ) : projectsToShow.length === 0 ? (
                                    <div className="text-xs text-slate-400 italic p-3">
                                        {projects.length === 0 ? "Aucun projet actif à ce jour." : "Tous les projets sont couverts par les espaces cochés."}
                                    </div>
                                ) : (
                                    <div className="max-h-56 overflow-y-auto rounded-md border p-2 mt-2 space-y-1">
                                        {spaces.map((s) => {
                                            if (selectedSpaceIds.includes(s.id)) return null;
                                            const inSpace = projectsToShow.filter((p) => p.space_id === s.id);
                                            if (inSpace.length === 0) return null;
                                            return (
                                                <div key={s.id} className="space-y-1">
                                                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold px-1 pt-1">{s.name}</div>
                                                    {inSpace.map((p) => {
                                                        const th = themes.find((t) => t.id === p.theme_id);
                                                        return (
                                                            <label key={p.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-slate-50 cursor-pointer">
                                                                <Checkbox
                                                                    checked={selectedProjectIds.includes(p.id)}
                                                                    onCheckedChange={() => toggleProject(p.id)}
                                                                    data-testid={`hub-access-project-${p.id}`}
                                                                />
                                                                {th && <span className="w-2 h-2 rounded-full" style={{ backgroundColor: th.color }} />}
                                                                <span className="text-xs text-slate-700 flex-1">{p.name}</span>
                                                                {th && <span className="text-[10px] text-slate-400">{th.name}</span>}
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {selectedSpaceIds.length === 0 && selectedProjectIds.length === 0 && (
                                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                                    ⚠️ Aucun espace ni projet sélectionné : l&apos;utilisateur verra le menu Projets mais aucun contenu.
                                </div>
                            )}
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
                        <Button onClick={submit} disabled={saving} data-testid="hub-access-submit">
                            {saving ? 'Enregistrement…' : 'Enregistrer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

function AccessOption({ value, selected, onSelect, title, description }) {
    const active = selected === value;
    return (
        <button
            type="button"
            onClick={() => onSelect(value)}
            className={cn(
                "w-full text-left rounded-lg border p-3 transition-all",
                active ? "border-indigo-500 bg-indigo-50/70 ring-2 ring-indigo-200" : "border-slate-200 hover:border-slate-300"
            )}
            data-testid={`hub-access-opt-${value}`}
        >
            <div className="flex items-start gap-2">
                <div className={cn("w-4 h-4 rounded-full border-2 mt-0.5", active ? "border-indigo-500 bg-indigo-500" : "border-slate-300")}>
                    {active && <div className="w-2 h-2 bg-white rounded-full m-auto mt-[3px]" />}
                </div>
                <div className="flex-1">
                    <div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                        {title}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">{description}</div>
                </div>
            </div>
        </button>
    );
}
